// REUN-02: resolve the threads shown under each meeting step, from the two
// subscriptions that can carry them.
//
// The rule this module owns used to live inline in an effect of
// MeetingThreadsDragDropContext, which put it out of reach of the tests — and
// that is exactly how a dedup regression slipped through twice. The dependency
// on React is gone: everything here is derived from its arguments.
//
// Each id of a step's agenda ends up in one of three states, and the whole
// point is to tell the last two apart:
//   - resolved: we hold the thread (with its meeting note, or without it yet);
//   - pending: the subscription result doesn't cover that id yet, so we simply
//     omit it — the row shows up once the data lands;
//   - not allowed: the result does cover it, it isn't there, and we know of it
//     nowhere else. That one is a real permission gap, and only that one earns
//     the padlock.
// Before this module, "pending" was reported as "not allowed": the padlock
// flashed on a thread someone else had just put on the agenda.

export interface ResolutionStep {
  id: string
  threadsIds?: readonly string[] | null
}

export interface NotAllowedEntry {
  id: string
  notAllowed: true
}

export interface ResolveStepThreadsParams<T extends { id: string }> {
  // Steps in agenda order; ids are deduplicated in that order.
  steps: readonly ResolutionStep[]
  // Threads resolved with their meeting note (the richest source).
  withNoteById: ReadonlyMap<string, T>
  // Threads from the picker scope, already shaped by the caller.
  inScopeById: ReadonlyMap<string, T>
  // What is currently on screen, so an already resolved id never regresses
  // while a subscription is in flight.
  prevByStep: Readonly<Record<string, readonly (T | NotAllowedEntry)[]>>
  // Ids the current subscription result actually covers, or null when that
  // result is stale. Null means "we know nothing", never "not allowed".
  coveredIds: ReadonlySet<string> | null
}

export interface ResolveStepThreadsResult<T extends { id: string }> {
  threadsByStep: Record<string, (T | NotAllowedEntry)[]>
  // Every id seen on the agenda, pending and not-allowed ones included: what
  // the caller subtracts from the picker to build `availableThreads`.
  keptIds: Set<string>
}

// Every id on the agenda, deduplicated in agenda order. Same rule as the
// dedup inside resolveStepThreads, deliberately shared: the caller derives
// its subscription variables from this, and resolveStepThreads decides what
// is "covered" against them. Two independent dedup rules would drift and
// silently break the pending state.
export function collectAgendaIds(steps: readonly ResolutionStep[]): string[] {
  const ids: string[] = []
  const seen = new Set<string>()
  for (const step of steps) {
    for (const threadId of step.threadsIds ?? []) {
      if (seen.has(threadId)) continue
      seen.add(threadId)
      ids.push(threadId)
    }
  }
  return ids
}

export function resolveStepThreads<T extends { id: string }>({
  steps,
  withNoteById,
  inScopeById,
  prevByStep,
  coveredIds,
}: ResolveStepThreadsParams<T>): ResolveStepThreadsResult<T> {
  const keptIds = new Set<string>()
  const threadsByStep: Record<string, (T | NotAllowedEntry)[]> = {}

  for (const step of steps) {
    const stepThreads: (T | NotAllowedEntry)[] = []
    const prevStepThreads = prevByStep[step.id]

    for (const threadId of step.threadsIds ?? []) {
      // Dedup across steps AND within a step: an id belongs to its very first
      // occurrence, scanning steps in order then ids in order.
      if (keptIds.has(threadId)) continue
      keptIds.add(threadId)

      const withNote = withNoteById.get(threadId)
      if (withNote) {
        stepThreads.push(withNote)
        continue
      }

      const inScope = inScopeById.get(threadId)
      if (inScope) {
        stepThreads.push(inScope)
        continue
      }

      const prev = prevStepThreads?.find((thread) => thread.id === threadId)
      if (prev && !('notAllowed' in prev)) {
        stepThreads.push(prev)
        continue
      }

      // Nothing holds it. Stale result, or an id the result doesn't cover:
      // pending, so never call it forbidden. Keep whatever was on screen —
      // padlock included — instead of making a row vanish and come back on
      // every agenda change; omit only when there is nothing to keep.
      if (!coveredIds || !coveredIds.has(threadId)) {
        if (prev) stepThreads.push(prev)
        continue
      }

      stepThreads.push({ id: threadId, notAllowed: true })
    }

    threadsByStep[step.id] = stepThreads
  }

  return { threadsByStep, keptIds }
}
